import { useMemo, useEffect } from "react";
import { Helmet as ReactHelmet } from "react-helmet";

import { useStatContext } from "soapbox/contexts/stat-context.tsx";
import { useAppSelector } from "soapbox/hooks/useAppSelector.ts";
import { useInstance } from "soapbox/hooks/useInstance.ts";
import { useSettings } from "soapbox/hooks/useSettings.ts";
import { RootState } from "soapbox/store.ts";
import FaviconService from "soapbox/utils/favicon-service.ts";

const DEFAULT_FAVICON =
  "https://media.truemaple.net/media/47af8e98-d004-42b1-9377-2a7ba189a215/favicon.png";

const getFaviconHref = (thumbnailUrl?: string) => {
  if (!thumbnailUrl) return DEFAULT_FAVICON;

  try {
    const parsed = new URL(thumbnailUrl, window.location.origin);

    if (parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }

    return parsed.href;
  } catch (_error) {
    return DEFAULT_FAVICON;
  }
};

const getNotifTotals = (state: RootState): number => {
  const notifications = state.notifications.unread || 0;
  const reports = state.admin.openReports.count();
  const approvals = state.admin.awaitingApproval.count();
  return notifications + reports + approvals;
};

interface IHelmet {
  children: React.ReactNode;
}

const Helmet: React.FC<IHelmet> = ({ children }) => {
  const { instance } = useInstance();
  const { unreadChatsCount } = useStatContext();
  const unreadCount = useAppSelector(
    (state) => getNotifTotals(state) + unreadChatsCount,
  );
  const { demetricator } = useSettings();

  const hasUnreadNotifications = useMemo(
    () => !(unreadCount < 1 || demetricator),
    [unreadCount, demetricator],
  );

  const addCounter = (string: string) => {
    return hasUnreadNotifications ? `(${unreadCount}) ${string}` : string;
  };
  const faviconSource =
    instance.icon.find((icon) => Boolean(icon.src))?.src ||
    instance.thumbnail?.url;
  const faviconHref = useMemo(
    () => getFaviconHref(faviconSource),
    [faviconSource],
  );

  useEffect(() => {
    // Rebind the favicon badge service to the latest favicon URL.
    FaviconService.initFaviconService();
    if (hasUnreadNotifications) {
      FaviconService.drawFaviconBadge();
    } else {
      FaviconService.clearFaviconBadge();
    }
  }, [faviconHref, hasUnreadNotifications]);

  return (
    <ReactHelmet
      titleTemplate={addCounter(`%s | ${instance.title}`)}
      defaultTitle={addCounter(instance.title)}
      defer={false}
    >
      <link rel="icon" href={faviconHref} />
      <link rel="shortcut icon" href={faviconHref} />
      <link rel="apple-touch-icon" href={faviconHref} />
      {children}
    </ReactHelmet>
  );
};

export default Helmet;
